import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  Divider,
  Container,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  AccountBalance as AuditIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import ConfirmationDialog from "../utils/dialog/confirmationDialog";

const SOLUTIONS = [
  {
    id: "in-house",
    name: "In-house Tax Practitioner",
    subtitle: "Perfect for Small & Medium Enterprises",
    price: "$15",
    description:
      "Ideal for teams preparing tax computation for small corporate groups",
    features: [
      {
        text: "Accesst to most features",
        icon: <CheckCircleIcon fontSize='small' color='success' />,
      },
      {
        text: "Create up to 5 companies",
        icon: <BusinessIcon fontSize='small' color='primary' />,
      },
      {
        text: "Incremental cost at only $3 / company",
        icon: <CheckCircleIcon fontSize='small' color='success' />,
      },
      {
        text: "Auto Referencing & Formatting",
        icon: <CheckCircleIcon fontSize='small' color='success' />,
      },
    ],
    badges: ["30-day Trial", "Best for SMEs"],
    buttonColor: "#c03b85",
    recommended: false,
    ctaGuest: "Start free trial",
    ctaRegistered: "Switch to this plan",
  },
  {
    id: "professional",
    name: "Tax Professional",
    subtitle: "Audit Firms & Large Corporates",
    price: "$60",
    description:
      "Ideal for tax department of audit firms or large corporate groups",
    features: [
      {
        text: "Full access to all features",
        icon: <CheckCircleIcon fontSize='small' color='success' />,
      },
      {
        text: "Create unlimited number of companies",
        icon: <AuditIcon fontSize='small' color='primary' />,
      },
      {
        text: "Bulk Upload & Export Functions",
        icon: <CheckCircleIcon fontSize='small' color='success' />,
      },
      {
        text: "Priority Support & IRAS Integration",
        icon: <CheckCircleIcon fontSize='small' color='success' />,
      },
    ],
    badges: ["Open Beta 25% OFF"],
    buttonColor: "#603F97",
    recommended: true,
    ctaGuest: "Start free trial",
    ctaRegistered: "Switch to this plan",
  },
];

/**
 * Map marketing cards to API plan UUIDs from `GET /subscription/getPlans`.
 */
export function resolveApiPlanId(solutionId, apiPlans) {
  if (!Array.isArray(apiPlans) || apiPlans.length === 0) return null;
  if (solutionId === "in-house") {
    const hit = apiPlans.find((p) =>
      /in[\s-]?house|inhouse|practitioner|sme/i.test(String(p.name || "")),
    );
    return hit?.id ?? null;
  }
  if (solutionId === "professional") {
    const hit = apiPlans.find((p) =>
      /tax\s*professional|professional|audit|large/i.test(String(p.name || "")),
    );
    return hit?.id ?? null;
  }
  return null;
}

const PlansTab = ({
  isGuestForTrial = false,
  apiPlans = [],
  plansLoading = false,
  onChangePlan,
  onPlanIdMissing,
  currentPlanId,
  pendingPlanId,
  ctaBusy = false,
}) => {
  const [confirmPlanChange, setConfirmPlanChange] = useState(null);

  const currentSolution = SOLUTIONS.find((solution) => {
    const resolvedPlanId = resolveApiPlanId(solution.id, apiPlans);
    return resolvedPlanId && currentPlanId && resolvedPlanId === currentPlanId;
  });

  const handleCta = (plan) => {
    //TODO: handle start trial exception
    if (isGuestForTrial) {
      return;
    }
    const planId = resolveApiPlanId(plan.id, apiPlans);
    if (!planId) {
      onPlanIdMissing?.();
      return;
    }
    if (planId === currentPlanId && !pendingPlanId) {
      return;
    }
    setConfirmPlanChange({ plan, planId });
  };

  const handleConfirmPlanChange = () => {
    if (!confirmPlanChange) return;
    onChangePlan?.(confirmPlanChange.planId);
  };

  const isDowngradeToInHouse =
    currentSolution?.id === "professional" &&
    confirmPlanChange?.plan.id === "in-house";

  const planChangeDescription = isDowngradeToInHouse
    ? "You are switching from Tax Professional to In-house Tax Practitioner. Some Tax Professional features will no longer be available, and user permissions may be reduced or changed for your team. Please review your users and access settings after the plan change."
    : confirmPlanChange
      ? `You are about to switch to the ${confirmPlanChange.plan.name} plan. Your subscription and available features may change from the next billing cycle.`
      : "";

  const closeConfirmPlanChange = () => {
    setConfirmPlanChange(null);
  };

  return (
    <>
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Grid container spacing={4} justifyContent='center'>
          {SOLUTIONS.map((plan) => {
            const resolvedPlanId = resolveApiPlanId(plan.id, apiPlans);
            const isCurrent =
              resolvedPlanId &&
              currentPlanId &&
              resolvedPlanId === currentPlanId &&
              !pendingPlanId;
            const isPendingNext =
              resolvedPlanId &&
              pendingPlanId &&
              resolvedPlanId === pendingPlanId;
            const ctaLabel = isGuestForTrial
              ? plan.ctaGuest
              : plan.ctaRegistered;
            const disableCta =
              ctaBusy ||
              plansLoading ||
              isCurrent ||
              isPendingNext ||
              (!isGuestForTrial &&
                !plansLoading &&
                (apiPlans.length === 0 || !resolvedPlanId));

            return (
              <Grid item xs={12} sm={10} md={6} lg={5} key={plan.id}>
                <Paper
                  elevation={6}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    backgroundColor: "#ffffff",
                    transition: "0.3s",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    ...(plan.recommended && {
                      borderColor: "#603F97",
                      borderWidth: 2,
                      borderStyle: "solid",
                      position: "relative",
                      top: -4,
                      boxShadow: "0 8px 24px rgba(25, 118, 210, 0.2)",
                    }),
                  }}
                >
                  {plan.recommended && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bgcolor: "#603F97",
                        color: "white",
                        px: 2,
                        py: 0.5,
                        borderTopRightRadius: 8,
                        borderBottomLeftRadius: 8,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                      }}
                    >
                      POPULAR
                    </Box>
                  )}

                  <Stack spacing={2} sx={{ height: "100%" }}>
                    <Stack direction='row' spacing={1} flexWrap='wrap' gap={1}>
                      {plan.badges.map((badge, i) => (
                        <Chip
                          key={i}
                          label={badge}
                          size='small'
                          sx={{
                            fontWeight: 600,
                            ...(badge.includes("OFF") && {
                              bgcolor: "#f44336",
                              color: "white",
                              "& .MuiChip-label": { fontWeight: 800 },
                            }),
                            ...(badge === "MOST POPULAR" && {
                              bgcolor: "#603F97",
                              color: "white",
                            }),
                          }}
                        />
                      ))}
                      {isCurrent && (
                        <Chip
                          label='Current plan'
                          size='small'
                          color='success'
                          sx={{ fontWeight: 700 }}
                        />
                      )}
                      {isPendingNext && (
                        <Chip
                          label='Scheduled next cycle'
                          size='small'
                          color='info'
                          sx={{ fontWeight: 700 }}
                        />
                      )}
                    </Stack>

                    <Typography
                      variant='h6'
                      fontWeight='bold'
                      color='text.primary'
                    >
                      {plan.name}
                    </Typography>

                    <Typography
                      variant='body2'
                      color='text.secondary'
                      gutterBottom
                    >
                      {plan.subtitle}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant='h3'
                        fontWeight='bold'
                        color='#1b5e20'
                      >
                        {plan.price}
                        {plan.originalPrice != null && (
                          <Typography
                            component='span'
                            variant='h6'
                            color='text.secondary'
                            sx={{ ml: 1, textDecoration: "line-through" }}
                          >
                            {plan.originalPrice}
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        / user / month
                      </Typography>
                    </Box>

                    <Typography
                      variant='body1'
                      color='text.secondary'
                      sx={{ mb: 2, flexGrow: 1 }}
                    >
                      {plan.description}
                    </Typography>

                    <Divider sx={{ my: 1 }} />

                    <List dense>
                      {plan.features.map((feature, i) => (
                        <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                          <ListItemIcon
                            sx={{ minWidth: 36, color: "inherit" }}
                          >
                            {feature.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={feature.text}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Box mt={4} gap={2} display='flex' flexDirection='column'>
                      <Button
                        variant='contained'
                        fullWidth
                        disabled={disableCta}
                        onClick={() => handleCta(plan)}
                        sx={{
                          borderRadius: "999px",
                          backgroundColor: plan.buttonColor,
                          textTransform: "none",
                          fontWeight: 600,
                          py: 1.5,
                          "&:hover": {
                            backgroundColor: plan.buttonColor,
                            opacity: 0.9,
                            transform: "translateY(-1px)",
                            boxShadow: 4,
                          },
                        }}
                      >
                        {ctaBusy || plansLoading ? (
                          <CircularProgress size={22} color='inherit' />
                        ) : (
                          ctaLabel
                        )}
                      </Button>

                      <Button
                        variant='text'
                        fullWidth
                        href='https://tax-savy.odoo.com/contactus'
                        target='_blank'
                        rel='noopener noreferrer'
                        sx={{
                          textTransform: "none",
                          color: "text.secondary",
                          fontWeight: 500,
                        }}
                      >
                        Contact Sales
                      </Button>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      <ConfirmationDialog
        open={Boolean(confirmPlanChange)}
        title={isDowngradeToInHouse ? "Confirm plan downgrade" : "Switch plan"}
        description={planChangeDescription}
        confirmLabel='Switch plan'
        cancelLabel='Keep current plan'
        confirmColor={isDowngradeToInHouse ? "error" : "primary"}
        onClose={closeConfirmPlanChange}
        onConfirm={handleConfirmPlanChange}
      />
    </>
  );
};

export default PlansTab;
